import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import * as Sentry from '@sentry/browser';
import { Icon, Spinner } from '@blueprintjs/core';
import { Alert, Button, CircularProgress, PhotoRow, PhotoUpload } from '@opendoor/mosaic';
import { WorkOrderMessages, WorkOrderMessagesProvider } from '@opendoor/tessellation';

import { useGetWorkOrder, useGetWorkOrderMessageThread } from '../graphql/queries/homework';
import { usePostNotesAndPhotos } from '../hooks/usePostNotesAndPhotos';
import { WEB_URL } from '../utils/constants';
import { isOlderThan48Hours } from '../utils/date';
import NotFoundErrorPage from './NotFoundErrorPage';

const OnSiteWorkerProgress = () => {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const [loading, setLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [files, setFiles] = useState([] as File[]);
  const [threadWorkOrderId, setThreadWorkOrderId] = useState<string | null>(null);

  const [postNotesAndPhotos] = usePostNotesAndPhotos();
  const { data: workOrder } = useGetWorkOrder({ variables: { id: workOrderId ?? '' } });

  // using state and useEffect to handle the case where the workOrderId is not available immediately
  // from the previous getWorkOrder query
  useEffect(() => {
    if (workOrder?.id) {
      setThreadWorkOrderId(workOrder.id);
    }
  }, [workOrder?.id]);

  const {
    data: threads,
    loading: messageThreadLoading,
    error: messageThreadError,
  } = useGetWorkOrderMessageThread({
    variables: { workOrderId: threadWorkOrderId ?? '' },
    skip: !threadWorkOrderId,
  });

  if (messageThreadError) {
    return <NotFoundErrorPage />;
  }

  if (messageThreadLoading) {
    return <CircularProgress className="mh-auto" size={72} thickness={5} />;
  }

  let needsUpdate = isOlderThan48Hours(threads?.onSiteMessageThread?.notes?.[0]?.createdAt ?? null);

  const removePhoto = (index: number) => {
    const newPhotos = [...files];
    newPhotos.splice(index, 1);
    setFiles(newPhotos);
  };

  const onSubmitNotes = async () => {
    setLoading(true);

    try {
      const status = await postNotesAndPhotos(workOrderId ?? '', newMessage, files, true);
      if (status) {
        setNewMessage('');
        setFiles([]);
        needsUpdate = false;
      }
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag('source', 'Notes');
        Sentry.captureException(error);
      });
    }

    setLoading(false);
  };

  const submitButton = () => {
    return (
      <Button
        color="primary"
        variant="contained"
        onClick={onSubmitNotes}
        disabled={!newMessage || loading}
        style={styles.buttons}
      >
        {loading ? <Spinner size={20} /> : 'Send'}
      </Button>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.topBar}>
        <div>
          <a
            className="ml-3 black"
            href={WEB_URL + `/on-site/work-requests/work-order/${workOrderId ?? ''}`}
          >
            <Icon icon="chevron-left" size={18} />
          </a>
          <span style={styles.topBarText}> Progress Updates </span>
        </div>
        {needsUpdate && (
          <Alert style={styles.alertBox} severity="error" variant="outlined">
            No updates in 48 hours
          </Alert>
        )}
      </div>
      <div style={styles.content}>
        <WorkOrderMessagesProvider
          workOrderId={workOrderId ?? ''}
          isOnSite={true}
          loading={loading}
        >
          <WorkOrderMessages />
        </WorkOrderMessagesProvider>
      </div>
      <div style={styles.footer}>
        <textarea
          data-testid="tp-notes-text-field"
          placeholder="Add a new note"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          rows={4}
          style={styles.textBox}
        />
        <div className="flex justify-around mv-4 mh-2">
          <PhotoUpload
            onSave={(uploadedFiles) => {
              const tempFiles = [...files, ...uploadedFiles];
              setFiles(tempFiles);
            }}
            buttonLabel="Add photos"
            style={styles.buttons}
            className="bg-white"
          />
          {submitButton()}
        </div>
        {files.length > 0 && (
          <div className="ml-5">
            <hr style={styles.ruler}></hr>
            <PhotoRow
              thumbnailHeight={80}
              thumbnailWidth={80}
              photoUrls={files.map((file) => {
                return URL.createObjectURL(file);
              })}
              onRemovePhoto={removePhoto}
              isMobile
            />
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as 'column',
    height: '100vh',
  },
  topBar: {
    backgroundColor: '#F7F7F9',
    height: '64px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: '0 0 auto',
  },
  topBarText: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#000000',
    marginLeft: '13px',
  },
  content: {
    flex: '1 1 auto',
    overflowY: 'scroll' as 'scroll',
    marginLeft: '0.5rem',
    marginRight: '0.5rem',
  },
  alertBox: {
    fontSize: '11px',
    marginRight: '10px',
    backgroundColor: '#F3F3F3',
    padding: '1px 8px',
  },
  textBox: {
    width: '93%',
    backgroundColor: '#FFFFFF',
    borderRadius: '10px',
    border: '1px solid black',
  },
  buttons: {
    width: '45%',
  },
  footer: {
    width: '100%',
    display: 'inline-block',
    textAlign: 'center' as 'center',
    backgroundColor: '#F7F7F9',
    padding: '20px 0 10px',
    flex: '0 0 auto',
  },
  ruler: {
    width: '95%',
    border: '0.5px solid #D8D8D8',
    margin: '15px 0',
  },
};

export default OnSiteWorkerProgress;
